import { createReducer, on } from '@ngrx/store';
import {
  Request,
  RequestFilter,
  PagedResult,
  RequestDetail,
  ProcessRequestForUluroResult,
  UserListItem,
  RequestStatusOption,
  PrintSiteOption,
  PrintLocationDetailOption,
} from '../../core';
import { PrintLocationOption } from '../../core/models/process-request.model';
import { RequestFilterOptions } from '../../infrastructure/http/repositories/requests.repository';
import {
  RequestsPageActions,
  RequestsAPIActions,
  RequestDetailPageActions,
  RequestDetailAPIActions,
  CreateRequestWizardActions,
} from './requests.actions';

export interface RequestsState {
  // List state
  requests: PagedResult<Request> | null;
  loading: boolean;
  error: string | null;
  currentFilter: RequestFilter;

  // Filter options
  filterOptions: RequestFilterOptions | null;
  filterOptionsLoading: boolean;

  // Detail state
  currentRequest: RequestDetail | null;
  currentRequestLoading: boolean;
  currentRequestError: string | null;

  // Print locations (for process modal)
  printLocations: PrintLocationOption[];
  printLocationsLoading: boolean;

  // Print locations by site (for request details)
  printLocationsBySite: PrintLocationDetailOption[];
  printLocationsBySiteLoading: boolean;

  // User list for assignment
  userList: UserListItem[];
  userListLoading: boolean;

  // Request statuses
  requestStatuses: RequestStatusOption[];
  requestStatusesLoading: boolean;

  // Print sites
  printSites: PrintSiteOption[];
  printSitesLoading: boolean;

  // Update request state
  updateRequestLoading: boolean;
  updateRequestError: string | null;

  // Process request state
  processRequestLoading: boolean;
  processRequestError: string | null;
  processRequestResult: ProcessRequestForUluroResult | null;

  // Create request wizard state
  createRequestLoading: boolean;
  createRequestError: string | null;
  createdRequestId: number | null;
}

const defaultFilter: RequestFilter = {
  page: 1,
  pageSize: 25,
  sort: '-requestDate',
};

export const initialState: RequestsState = {
  requests: null,
  loading: false,
  error: null,
  currentFilter: defaultFilter,
  filterOptions: null,
  filterOptionsLoading: false,
  currentRequest: null,
  currentRequestLoading: false,
  currentRequestError: null,
  printLocations: [],
  printLocationsLoading: false,
  printLocationsBySite: [],
  printLocationsBySiteLoading: false,
  userList: [],
  userListLoading: false,
  requestStatuses: [],
  requestStatusesLoading: false,
  printSites: [],
  printSitesLoading: false,
  updateRequestLoading: false,
  updateRequestError: null,
  processRequestLoading: false,
  processRequestError: null,
  processRequestResult: null,
  createRequestLoading: false,
  createRequestError: null,
  createdRequestId: null,
};

export const requestsReducer = createReducer(
  initialState,

  // Load Requests
  on(RequestsPageActions.loadRequests, (state, { filter }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, ...filter },
  })),

  on(RequestsPageActions.search, (state, { searchTerm }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, search: searchTerm, page: 1 },
  })),

  on(RequestsPageActions.sort, (state, { sortField }) => {
    const currentSort = state.currentFilter.sort ?? '';
    let newSort: string;
    if (currentSort === sortField) {
      newSort = `-${sortField}`;
    } else if (currentSort === `-${sortField}`) {
      newSort = sortField;
    } else {
      newSort = sortField;
    }
    return {
      ...state,
      loading: true,
      error: null,
      currentFilter: { ...state.currentFilter, sort: newSort, page: 1 },
    };
  }),

  on(RequestsPageActions.changePage, (state, { page }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, page },
  })),

  on(RequestsPageActions.changePageSize, (state, { pageSize }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, pageSize, page: 1 },
  })),

  on(RequestsPageActions.applyFilters, (state, { filters }) => {
    // The component always sends the full set of currently active filters,
    // so replace the filter state entirely (keeping only paging/sort metadata)
    // rather than merging - otherwise a cleared filter would never be removed
    // from currentFilter since its key would simply be absent from `filters`.
    const definedFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== undefined)
    );
    return {
      ...state,
      loading: true,
      error: null,
      currentFilter: {
        pageSize: state.currentFilter.pageSize,
        sort: state.currentFilter.sort,
        ...definedFilters,
        page: 1,
      },
    };
  }),

  on(RequestsPageActions.clearFilters, state => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: defaultFilter,
  })),

  on(RequestsPageActions.refresh, state => ({
    ...state,
    loading: true,
    error: null,
  })),

  // API responses
  on(RequestsAPIActions.loadRequestsSuccess, (state, { result }) => ({
    ...state,
    requests: result,
    loading: false,
    error: null,
  })),

  on(RequestsAPIActions.loadRequestsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Filter options
  on(RequestsAPIActions.loadFilterOptions, state => ({
    ...state,
    filterOptionsLoading: true,
  })),

  on(RequestsAPIActions.loadFilterOptionsSuccess, (state, { options }) => ({
    ...state,
    filterOptions: options,
    filterOptionsLoading: false,
  })),

  on(RequestsAPIActions.loadFilterOptionsFailure, state => ({
    ...state,
    filterOptionsLoading: false,
  })),

  // Request Detail
  on(RequestDetailPageActions.loadRequestDetail, state => ({
    ...state,
    currentRequest: null,
    currentRequestLoading: true,
    currentRequestError: null,
  })),

  on(
    RequestDetailAPIActions.loadRequestDetailSuccess,
    (state, { request }) => ({
      ...state,
      currentRequest: request,
      currentRequestLoading: false,
      currentRequestError: null,
    })
  ),

  on(RequestDetailAPIActions.loadRequestDetailFailure, (state, { error }) => ({
    ...state,
    currentRequestLoading: false,
    currentRequestError: error,
  })),

  on(
    RequestDetailAPIActions.loadRequestDetailNotFound,
    (state, { requestNumber }) => ({
      ...state,
      currentRequestLoading: false,
      currentRequestError: `Request #${requestNumber} not found`,
    })
  ),

  on(RequestDetailPageActions.clearCurrentRequest, state => ({
    ...state,
    currentRequest: null,
    currentRequestError: null,
  })),

  // Print Locations
  on(RequestDetailPageActions.loadPrintLocations, state => ({
    ...state,
    printLocationsLoading: true,
  })),

  on(
    RequestDetailAPIActions.loadPrintLocationsSuccess,
    (state, { printLocations }) => ({
      ...state,
      printLocations,
      printLocationsLoading: false,
    })
  ),

  on(RequestDetailAPIActions.loadPrintLocationsFailure, state => ({
    ...state,
    printLocationsLoading: false,
  })),

  // Print Locations By Site
  on(RequestDetailPageActions.loadPrintLocationsBySite, state => ({
    ...state,
    printLocationsBySiteLoading: true,
  })),

  on(
    RequestDetailAPIActions.loadPrintLocationsBySiteSuccess,
    (state, { printLocations }) => ({
      ...state,
      printLocationsBySite: printLocations,
      printLocationsBySiteLoading: false,
    })
  ),

  on(RequestDetailAPIActions.loadPrintLocationsBySiteFailure, state => ({
    ...state,
    printLocationsBySiteLoading: false,
  })),

  // Update Request
  on(RequestDetailPageActions.updateRequest, state => ({
    ...state,
    updateRequestLoading: true,
    updateRequestError: null,
  })),

  on(RequestDetailAPIActions.updateRequestSuccess, state => ({
    ...state,
    updateRequestLoading: false,
    updateRequestError: null,
  })),

  on(RequestDetailAPIActions.updateRequestFailure, (state, { error }) => ({
    ...state,
    updateRequestLoading: false,
    updateRequestError: error,
  })),

  // Process Request
  on(RequestDetailPageActions.processRequest, state => ({
    ...state,
    processRequestLoading: true,
    processRequestError: null,
    processRequestResult: null,
  })),

  on(RequestDetailAPIActions.processRequestSuccess, (state, { result }) => ({
    ...state,
    processRequestLoading: false,
    processRequestError: null,
    processRequestResult: result,
  })),

  on(RequestDetailAPIActions.processRequestFailure, (state, { error }) => ({
    ...state,
    processRequestLoading: false,
    processRequestError: error,
    processRequestResult: null,
  })),

  on(RequestDetailPageActions.clearProcessResult, state => ({
    ...state,
    processRequestResult: null,
  })),

  on(RequestDetailPageActions.clearProcessRequestError, state => ({
    ...state,
    processRequestError: null,
  })),

  // User List
  on(RequestDetailPageActions.loadUserList, state => ({
    ...state,
    userListLoading: true,
  })),

  on(RequestDetailAPIActions.loadUserListSuccess, (state, { userList }) => ({
    ...state,
    userList,
    userListLoading: false,
  })),

  on(RequestDetailAPIActions.loadUserListFailure, state => ({
    ...state,
    userListLoading: false,
  })),

  // Request Statuses
  on(RequestDetailPageActions.loadRequestStatuses, state => ({
    ...state,
    requestStatusesLoading: true,
  })),

  on(
    RequestDetailAPIActions.loadRequestStatusesSuccess,
    (state, { requestStatuses }) => ({
      ...state,
      requestStatuses,
      requestStatusesLoading: false,
    })
  ),

  on(RequestDetailAPIActions.loadRequestStatusesFailure, state => ({
    ...state,
    requestStatusesLoading: false,
  })),

  // Print Sites
  on(RequestDetailPageActions.loadPrintSites, state => ({
    ...state,
    printSitesLoading: true,
  })),

  on(
    RequestDetailAPIActions.loadPrintSitesSuccess,
    (state, { printSites }) => ({
      ...state,
      printSites,
      printSitesLoading: false,
    })
  ),

  on(RequestDetailAPIActions.loadPrintSitesFailure, state => ({
    ...state,
    printSitesLoading: false,
  })),

  // Create Request Wizard
  on(CreateRequestWizardActions.submitRequest, state => ({
    ...state,
    createRequestLoading: true,
    createRequestError: null,
    createdRequestId: null,
  })),

  on(CreateRequestWizardActions.submitSuccess, (state, { requestId }) => ({
    ...state,
    createRequestLoading: false,
    createRequestError: null,
    createdRequestId: requestId,
  })),

  on(CreateRequestWizardActions.submitFailure, (state, { error }) => ({
    ...state,
    createRequestLoading: false,
    createRequestError: error,
    createdRequestId: null,
  })),

  on(CreateRequestWizardActions.clearWizard, state => ({
    ...state,
    createRequestLoading: false,
    createRequestError: null,
    createdRequestId: null,
  }))
);
